import type { TFunction } from "i18next";
import { format, isValid, parseISO } from "date-fns";
import type { Locale } from "date-fns";

export const LEAVE_STATUS_FILTERS = [
  "Pending",
  "Approved",
  "Rejected",
  "Cancelled",
  "In Review",
  "Pending Employee Document",
  "Document Uploaded",
  "HR Review Required",
] as const;

const STATUS_KEY_BY_VALUE: Record<string, string> = {
  Pending: "pending",
  Approved: "approved",
  Rejected: "rejected",
  Cancelled: "cancelled",
  "In Review": "inReview",
  "Pending Employee Document": "pendingEmployeeDocument",
  "Document Uploaded": "documentUploaded",
  "HR Review Required": "hrReviewRequired",
  in_review: "inReview",
};

export function getLeaveStatusLabel(t: TFunction, status: string): string {
  const key = STATUS_KEY_BY_VALUE[status] ?? status.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return t(`leave.statuses.${key}`, { defaultValue: status });
}

export function getLeaveTypeLabel(
  t: TFunction,
  leaveType: string,
  configuredLabel?: string | null,
): string {
  const normalizedType = leaveType.trim().toLowerCase().replace(/[\s-]+/g, "_");
  const key = `leave.types.${normalizedType}`;
  const translated = t(key, { defaultValue: "" }).trim();
  const englishDefault = t(key, { lng: "en", defaultValue: "" }).trim();
  const configured = configuredLabel?.trim() ?? "";
  const configuredLower = configured.toLocaleLowerCase();
  const isRawIdentifier = configuredLower === leaveType.trim().toLocaleLowerCase();
  const isBuiltInEnglish = Boolean(
    configured && englishDefault && configuredLower === englishDefault.toLocaleLowerCase(),
  );

  // Preserve genuine tenant customizations. Seeded English labels still follow
  // the active application language through the built-in translations.
  if (configured && !isRawIdentifier && !isBuiltInEnglish) return configured;
  if (translated && translated !== key) return translated;
  if (configured && !isRawIdentifier) return configured;
  return t("leave.types.other");
}

export function formatLeaveDateRange(
  startDate: string,
  endDate: string,
  locale: Locale,
): string {
  const start = parseISO(startDate.slice(0, 10));
  const end = parseISO(endDate.slice(0, 10));
  if (!isValid(start) || !isValid(end)) return `${startDate} – ${endDate}`;

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const sameDay = sameMonth && start.getDate() === end.getDate();

  if (sameDay) return format(start, "d MMM yyyy", { locale });
  if (sameMonth) {
    return `${format(start, "d", { locale })}–${format(end, "d MMM yyyy", { locale })}`;
  }
  if (sameYear) {
    return `${format(start, "d MMM", { locale })} – ${format(end, "d MMM yyyy", { locale })}`;
  }
  return `${format(start, "d MMM yyyy", { locale })} – ${format(end, "d MMM yyyy", { locale })}`;
}

export function getLeaveApiErrorMessage(t: TFunction, err: unknown, fallbackKey?: string): string {
  const code = typeof err === "object" && err && "code" in err ? String((err as { code?: string }).code ?? "") : "";
  const message = err instanceof Error ? err.message : "";
  const translationKey = code || (message.startsWith("leave.") ? message : "");

  if (translationKey) {
    return t(translationKey, { defaultValue: message || t(fallbackKey ?? "leave.submitFailed") });
  }

  switch (message) {
    case "Cannot approve your own leave request":
    case "Cannot reject your own leave request":
    case "You cannot approve/reject your own requests":
      return t("leave.errors.selfApprovalRejection");
    case "leave.errors.overlappingRequest":
      return t("leave.errors.overlappingRequest");
    default:
      return message || (fallbackKey ? t(fallbackKey) : t("leave.submitFailed"));
  }
}
